import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Package, Plus, Edit, Trash2, Save, X, DollarSign,
  Users, Server, Building2, Wifi, HardDrive, Check
} from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SubscriptionPlansManager = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    plan_code: '',
    plan_name: '',
    monthly_price: 0,
    max_customers: 200,
    max_users: 5,
    max_mikrotiks: 4,
    max_branches: 4,
    max_vouchers: 5000,
    max_nas_vpn: 4,
    max_hotspot_active: 200,
    whatsapp_devices: 1,
    storage_limit_gb: 5,
    is_active: true
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/subscription-plans/custom`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPlans(data.data);
      } else {
        toast.error('Failed to fetch plans');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Error loading plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/subscription-plans/custom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Plan berhasil dibuat!');
        setShowAddModal(false);
        resetForm();
        fetchPlans();
      } else {
        toast.error(data.detail || 'Failed to create plan');
      }
    } catch (error) {
      console.error('Error creating plan:', error);
      toast.error('Error creating plan');
    }
  };

  const handleUpdatePlan = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/subscription-plans/custom/${planId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || 'Plan berhasil diupdate!');
        setEditingPlan(null);
        resetForm();
        fetchPlans();
      } else {
        toast.error(data.detail || 'Failed to update plan');
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error('Error updating plan');
    }
  };

  const handleDeletePlan = async (planId, planName) => {
    if (!window.confirm(`Hapus plan "${planName}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/subscription-plans/custom/${planId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Plan berhasil dihapus');
        fetchPlans();
      } else {
        toast.error(data.detail || 'Failed to delete plan');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error deleting plan');
    }
  };

  const startEdit = (plan) => {
    setEditingPlan(plan.id);
    setFormData({
      plan_code: plan.plan_code,
      plan_name: plan.plan_name,
      monthly_price: plan.monthly_price,
      max_customers: plan.max_customers,
      max_users: plan.max_users,
      max_mikrotiks: plan.max_mikrotiks,
      max_branches: plan.max_branches,
      max_vouchers: plan.max_vouchers,
      max_nas_vpn: plan.max_nas_vpn,
      max_hotspot_active: plan.max_hotspot_active,
      whatsapp_devices: plan.whatsapp_devices,
      storage_limit_gb: plan.storage_limit_gb,
      is_active: plan.is_active
    });
  };

  const cancelEdit = () => {
    setEditingPlan(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      plan_code: '',
      plan_name: '',
      monthly_price: 0,
      max_customers: 200,
      max_users: 5,
      max_mikrotiks: 4,
      max_branches: 4,
      max_vouchers: 5000,
      max_nas_vpn: 4,
      max_hotspot_active: 200,
      whatsapp_devices: 1,
      storage_limit_gb: 5,
      is_active: true
    });
  };

  const formatCurrency = (amount) => {
    return `Rp ${amount.toLocaleString('id-ID')}`;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen dark:bg-slate-900">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-slate-100">Subscription Plans Manager</h1>
          <p className="text-gray-600 dark:text-slate-300">Kelola paket langganan dan pricing untuk tenant</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Tambah Plan Baru
        </Button>
      </div>

      {/* Plans List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 dark:bg-slate-800 dark:border-slate-700">
        <div className="p-4 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Custom Plans</h2>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500 dark:text-slate-400">
            Loading plans...
          </div>
        ) : plans.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-slate-400">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Belum ada custom plan</p>
            <Button onClick={() => setShowAddModal(true)} className="mt-4" variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Buat Plan Pertama
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-slate-700">
            {plans.map((plan) => (
              <div key={plan.id} className="p-6 hover:bg-gray-50 dark:hover:bg-slate-700/50">
                {editingPlan === plan.id ? (
                  <EditPlanForm
                    formData={formData}
                    setFormData={setFormData}
                    onSave={() => handleUpdatePlan(plan.id)}
                    onCancel={cancelEdit}
                  />
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">{plan.plan_name}</h3>
                        <Badge className={plan.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                          {plan.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <span className="text-sm text-gray-500 dark:text-slate-400">Code: {plan.plan_code}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Harga/Bulan</p>
                            <p className="font-semibold text-gray-900 dark:text-slate-100">{formatCurrency(plan.monthly_price)}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Max PPPoE</p>
                            <p className="font-semibold text-gray-900 dark:text-slate-100">{plan.max_customers}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Server className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Max Mikrotik</p>
                            <p className="font-semibold text-gray-900 dark:text-slate-100">{plan.max_mikrotiks}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Max Branches</p>
                            <p className="font-semibold text-gray-900 dark:text-slate-100">{plan.max_branches}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Wifi className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Max Vouchers</p>
                            <p className="font-semibold text-gray-900 dark:text-slate-100">{plan.max_vouchers?.toLocaleString()}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Max Hotspot</p>
                            <p className="font-semibold text-gray-900 dark:text-slate-100">{plan.max_hotspot_active}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <HardDrive className="w-4 h-4 text-gray-600 dark:text-slate-300" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">Storage</p>
                            <p className="font-semibold text-gray-900 dark:text-slate-100">{plan.storage_limit_gb} GB</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Wifi className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">WA Devices</p>
                            <p className="font-semibold text-gray-900 dark:text-slate-100">{plan.whatsapp_devices}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => startEdit(plan)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:bg-blue-900/30"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePlan(plan.id, plan.plan_name)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded dark:text-red-400 dark:hover:bg-red-900/30"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Plan Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Tambah Plan Baru</h2>
            </div>

            <form onSubmit={handleCreatePlan} className="p-6">
              <PlanForm formData={formData} setFormData={setFormData} />
              
              <div className="flex gap-3 pt-4 border-t mt-6">
                <Button type="button" variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }} className="flex-1">
                  Batal
                </Button>
                <Button type="submit" className="flex-1">
                  <Save className="w-4 h-4 mr-2" />
                  Simpan Plan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Plan Form Component (reusable for create and edit)
const PlanForm = ({ formData, setFormData }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Plan Code * <span className="text-xs text-gray-500 dark:text-slate-400">(unique, lowercase)</span>
          </label>
          <Input
            required
            placeholder="custom-isp-plan"
            value={formData.plan_code}
            onChange={(e) => setFormData({...formData, plan_code: e.target.value.toLowerCase().replace(/\s/g, '-')})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Nama Plan *
          </label>
          <Input
            required
            placeholder="Custom ISP Plan 300"
            value={formData.plan_name}
            onChange={(e) => setFormData({...formData, plan_name: e.target.value})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Harga/Bulan (Rp) *
          </label>
          <Input
            required
            type="number"
            placeholder="350000"
            value={formData.monthly_price}
            onChange={(e) => setFormData({...formData, monthly_price: parseFloat(e.target.value)})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Max PPPoE Users *
          </label>
          <Input
            required
            type="number"
            placeholder="300"
            value={formData.max_customers}
            onChange={(e) => setFormData({...formData, max_customers: parseInt(e.target.value)})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Max Staff Users
          </label>
          <Input
            type="number"
            placeholder="5"
            value={formData.max_users}
            onChange={(e) => setFormData({...formData, max_users: parseInt(e.target.value)})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Max Mikrotik Routers
          </label>
          <Input
            type="number"
            placeholder="4"
            value={formData.max_mikrotiks}
            onChange={(e) => setFormData({...formData, max_mikrotiks: parseInt(e.target.value)})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Max Branches
          </label>
          <Input
            type="number"
            placeholder="4"
            value={formData.max_branches}
            onChange={(e) => setFormData({...formData, max_branches: parseInt(e.target.value)})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Max Vouchers
          </label>
          <Input
            type="number"
            placeholder="5000"
            value={formData.max_vouchers}
            onChange={(e) => setFormData({...formData, max_vouchers: parseInt(e.target.value)})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Max NAS/VPN
          </label>
          <Input
            type="number"
            placeholder="4"
            value={formData.max_nas_vpn}
            onChange={(e) => setFormData({...formData, max_nas_vpn: parseInt(e.target.value)})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Max Hotspot Active
          </label>
          <Input
            type="number"
            placeholder="200"
            value={formData.max_hotspot_active}
            onChange={(e) => setFormData({...formData, max_hotspot_active: parseInt(e.target.value)})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            WhatsApp Devices
          </label>
          <Input
            type="number"
            placeholder="1"
            value={formData.whatsapp_devices}
            onChange={(e) => setFormData({...formData, whatsapp_devices: parseInt(e.target.value)})}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
            Storage Limit (GB)
          </label>
          <Input
            type="number"
            placeholder="5"
            value={formData.storage_limit_gb}
            onChange={(e) => setFormData({...formData, storage_limit_gb: parseInt(e.target.value)})}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
          className="w-4 h-4 text-indigo-600 rounded dark:text-indigo-400"
        />
        <label htmlFor="is_active" className="text-sm font-medium text-gray-700 dark:text-slate-200">
          Plan Aktif (tersedia untuk dipilih)
        </label>
      </div>
    </div>
  );
};

// Edit Plan Form Component
const EditPlanForm = ({ formData, setFormData, onSave, onCancel }) => {
  return (
    <div className="space-y-4">
      <PlanForm formData={formData} setFormData={setFormData} />
      
      <div className="flex gap-2 pt-4 border-t">
        <Button onClick={onCancel} variant="outline" size="sm">
          <X className="w-4 h-4 mr-1" />
          Batal
        </Button>
        <Button onClick={onSave} size="sm">
          <Check className="w-4 h-4 mr-1" />
          Simpan
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionPlansManager;
